import { useMemo, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { getProfileTabKeys } from '@wlb/shared/constants';
import type { ProfileTabAction, ProfileTabKey } from '@wlb/shared/constants/profileTabs';
import useUserPosts, { type UseUserPostsResult } from '@wlb/shared/hooks/useUserPosts';
import type { TabItem } from '../../components/ui/Tabs';

interface ProfileTabsState {
  items: TabItem<ProfileTabKey>[];
  activeKey: ProfileTabKey;
  setActiveKey: (key: ProfileTabKey) => void;
  action: ProfileTabAction | null;
  posts: UseUserPostsResult;
}

/**
 * Tab strip state plus the post page for whichever tab is showing.
 * Activities is only offered on the signed-in member's own profile.
 */
export const useProfileTabs = (
  userId: number | null | undefined,
  isOwnProfile = true,
  userGuid?: string | null,
): ProfileTabsState => {
  const { t } = useTranslation();
  const tabs = useMemo(() => getProfileTabKeys(isOwnProfile), [isOwnProfile]);
  const [activeKey, setActiveKey] = useState<ProfileTabKey>(() => tabs[0].key);

  const items = useMemo(
    () => tabs.map(tab => ({ key: tab.key, label: t(`profile.tabs.${tab.key}`) })),
    [tabs, t],
  );

  const action = tabs.find(tab => tab.key === activeKey)?.action ?? null;
  const posts = useUserPosts(userId, action, userGuid);

  return { items, activeKey, setActiveKey, action, posts };
};
